<template>
  <div
    v-if="visible"
    class="sidebar-backdrop"
    @click.self="close"
  >
    <div
      class="sidebar-drawer sidebar-open"
      @click.stop
    >
      <div class="sticky-header d-flex align-items-center justify-content-between">
        <div>
          <span class="ticket-ref">{{ ticket && ticket.reference }}</span>
          <span :class="['status-pill', getStatusClass(ticket && ticket.status)]">{{ getStatusLabel(ticket && ticket.status) }}</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <!-- Primary contextual action -->
          <b-button
            v-if="primaryAction.label"
            ref="primaryBtn"
            size="sm"
            :variant="primaryAction.variant"
            class="mr-2"
            :disabled="disabledActions"
            @click="onPrimaryClick"
          >
            <i
              :class="primaryAction.icon"
              class="mr-1"
            /> {{ primaryAction.label }}
          </b-button>
          <!-- Secondary quick actions -->
          <b-button
            v-if="canResolve"
            size="sm"
            variant="outline-success"
            class="mr-1"
            :disabled="disabledActions"
            @click="confirmResolve"
          >
            <i class="fas fa-check mr-1" /> Résoudre
          </b-button>
          <b-button
            v-if="canEscalate"
            size="sm"
            variant="outline-warning"
            class="mr-1"
            :disabled="disabledActions"
            @click="confirmEscalate"
          >
            <i class="fas fa-exclamation-triangle mr-1" /> Escalader
          </b-button>
          <b-button
            v-if="canClose"
            size="sm"
            variant="outline-dark"
            class="mr-2"
            :disabled="disabledActions"
            @click="confirmClose"
          >
            <i class="fas fa-times mr-1" /> Fermer
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            :disabled="!ticket"
            @click="$emit('view-detail')"
          >
            <i class="fas fa-external-link-alt mr-1" /> Détail
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="close"
          >
            Fermer
          </b-button>
        </div>
      </div>
      <div class="sidebar-content">
        <!-- Main Info Card -->
        <div class="modern-card mb-3">
          <div class="info-item-premium">
            <div class="info-icon-premium">
              <b-avatar :src="(ticket.driver && ticket.driver.avatar) || defaultAvatar" />
            </div>
            <div>
              <div><strong>Livreur:</strong> {{ (ticket.driver && (ticket.driver.full_name || ticket.driver.username)) || '-' }}</div>
              <div><strong>Commande:</strong> {{ (ticket.order && (ticket.order.id || ticket.order.reference)) || '-' }}</div>
              <div><strong>Motif:</strong> {{ (ticket.reason_type && (ticket.reason_type.content || ticket.reason_type.label)) || '-' }}</div>
              <div><strong>Date:</strong> {{ formatDate(ticket.created_at) }}</div>
            </div>
          </div>
        </div>
        <!-- Order Details -->
        <div
          v-if="ticket && ticket.order"
          class="modern-card mb-3"
        >
          <div class="section-title">
            Commande
          </div>
          <div class="grid-2">
            <div><strong>BL:</strong> #{{ ticket.order.id }}</div>
            <div><strong>Montant:</strong> {{ ticket.order.price_ttc || '-' }} TND</div>
            <div><strong>Zones:</strong> {{ [ticket.order.governorate && ticket.order.governorate.name, ticket.order.delegation && ticket.order.delegation.name].filter(Boolean).join(' / ') || '-' }}</div>
            <div><strong>Fragile:</strong> {{ ticket.order.is_fragile ? 'Oui' : 'Non' }}</div>
          </div>
        </div>
        <!-- Client Details -->
        <div
          v-if="ticket && ticket.order"
          class="modern-card mb-3"
        >
          <div class="section-title">
            Client
          </div>
          <div class="grid-2">
            <div><strong>Nom:</strong> {{ ticket.order.name || '-' }}</div>
            <div><strong>Téléphones:</strong> {{ [ticket.order.phone1, ticket.order.phone2].filter(Boolean).join(' / ') || '-' }}</div>
            <div class="grid-span-2">
              <strong>Adresse:</strong> {{ ticket.order.address || '-' }}
            </div>
          </div>
        </div>
        <!-- Supplier Details -->
        <div
          v-if="ticket && ticket.order && ticket.order.supplier"
          class="modern-card mb-3"
        >
          <div class="section-title">
            Fournisseur
          </div>
          <div class="grid-2">
            <div><strong>Nom:</strong> {{ ticket.order.supplier.username || ticket.order.supplier.full_name || '-' }}</div>
            <div><strong>Téléphone:</strong> {{ ticket.order.supplier.phone || '-' }}</div>
          </div>
        </div>
        <!-- Contact Attempts -->
        <div
          v-if="ticket.contact_attempts && ticket.contact_attempts.length"
          class="modern-card mb-3"
        >
          <div><strong>Tentatives de contact:</strong></div>
          <ul>
            <li
              v-for="(attempt, idx) in ticket.contact_attempts"
              :key="idx"
            >
              {{ formatDate(attempt.date) }} par {{ (attempt.user && attempt.user.username) || '-' }}
            </li>
          </ul>
        </div>
        <!-- Status -->
        <div class="modern-card mb-3">
          <div><strong>Statut:</strong> <span
            :class="getStatusClass(ticket.status)"
            class="status-pill"
          >{{ getStatusLabel(ticket.status) }}</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BAvatar } from 'bootstrap-vue'
import defaultAvatar from '@/assets/images/avatars/user.png'

export default {
  name: 'DriverTicketQuickView',
  components: { BButton, BAvatar },
  props: {
    visible: { type: Boolean, default: false },
    ticket: { type: Object, default: null },
    loadingId: { type: [Number, String, null], default: null },
  },
  data() {
    return {
      defaultAvatar,
    }
  },
  computed: {
    disabledActions() {
      return !!this.loadingId
    },
    normalizedStatus() {
      const s = (this.ticket && this.ticket.status) ? String(this.ticket.status).toLowerCase() : ''
      return s
    },
    canResolve() {
      return this.normalizedStatus === 'in_progress' || this.normalizedStatus === 'escalated'
    },
    canEscalate() {
      return this.normalizedStatus === 'in_progress' || this.normalizedStatus === 'new'
    },
    canClose() {
      return this.normalizedStatus === 'resolved'
    },
    primaryAction() {
      if (this.normalizedStatus === 'new') {
        return {
          label: 'Prendre', icon: 'fas fa-play', variant: 'outline-primary', event: 'take',
        }
      }
      if (this.normalizedStatus === 'in_progress') {
        return {
          label: 'Ajouter contact', icon: 'fas fa-phone', variant: 'outline-info', event: 'add-contact',
        }
      }
      if (this.normalizedStatus === 'resolved') {
        return {
          label: 'Fermer', icon: 'fas fa-times', variant: 'outline-dark', event: 'close-ticket',
        }
      }
      return {
        label: 'Aperçu', icon: 'fas fa-columns', variant: 'outline-secondary', event: 'view-detail',
      }
    },
  },
  watch: {
    visible(val) {
      if (val) this.focusPrimary()
    },
    ticket() {
      if (this.visible) this.$nextTick(() => this.focusPrimary())
    },
  },
  mounted() {
    if (this.visible) this.focusPrimary()
  },
  methods: {
    close() {
      this.$emit('close')
    },
    async confirm(message) {
      if (!this.$bvModal || typeof this.$bvModal.msgBoxConfirm !== 'function') return true
      try {
        const res = await this.$bvModal.msgBoxConfirm(message, {
          title: 'Confirmation',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: 'Oui',
          cancelTitle: 'Non',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
        return !!res
      } catch (e) {
        return false
      }
    },
    async onPrimaryClick() {
      const ev = (this.primaryAction && this.primaryAction.event) || ''
      if (ev === 'close-ticket') return this.confirmClose()
      if (ev === 'resolve') return this.confirmResolve()
      if (ev === 'escalate') return this.confirmEscalate()
      this.$emit(ev)
      return null
    },
    async confirmResolve() {
      const ok = await this.confirm('Marquer ce ticket comme résolu ?')
      if (ok) this.$emit('resolve')
    },
    async confirmEscalate() {
      const ok = await this.confirm('Escalader ce ticket ?')
      if (ok) this.$emit('escalate')
    },
    async confirmClose() {
      const ok = await this.confirm('Fermer ce ticket ?')
      if (ok) this.$emit('close-ticket')
    },
    focusPrimary() {
      this.$nextTick(() => {
        const el = this.$refs.primaryBtn
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    getStatusClass(status) {
      if (!status) return 'status-open'
      const s = String(status).toLowerCase()
      if (s === 'new') return 'status-open'
      if (s === 'in_progress') return 'status-progress'
      if (s === 'resolved') return 'status-resolved'
      if (s === 'closed') return 'status-closed'
      if (s === 'escalated') return 'status-escalated'
      return 'status-open'
    },
    getStatusLabel(status) {
      if (!status) return '-'
      const s = String(status).toLowerCase()
      if (s === 'new') return 'Nouveau'
      if (s === 'in_progress') return 'En cours'
      if (s === 'resolved') return 'Résolu'
      if (s === 'closed') return 'Fermé'
      if (s === 'escalated') return 'Escaladé'
      return status
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
/* Local styles for the quick view drawer; removed invalid import of a .vue file */
.sidebar-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 1050; }
.sidebar-drawer { position: absolute; right: 0; top: 0; bottom: 0; width: 420px; max-width: 95vw; background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.12); display: flex; flex-direction: column; }
.sticky-header { position: sticky; top: 0; padding: 0.75rem 1rem; background: #f8f9fa; border-bottom: 1px solid #e9ecef; z-index: 1; }
.sidebar-content { padding: 1rem; overflow-y: auto; }
.ticket-ref { font-weight: 600; margin-right: .5rem; }
.status-pill { display: inline-flex; align-items: center; gap: .35rem; padding: .2rem .6rem; border-radius: 999px; font-size: .75rem; font-weight: 600; }
.status-open { background: #fef2f2; color: #b91c1c; }
.status-progress { background: #fff7ed; color: #c2410c; }
.status-resolved { background: #ecfdf5; color: #047857; }
.status-closed { background: #eef2ff; color: #3730a3; }
.status-escalated { background: #fee2e2; color: #991b1b; }
.modern-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: .75rem; }
.info-item-premium { display: flex; align-items: center; gap: .75rem; }
.info-icon-premium { display: flex; align-items: center; justify-content: center; }
.gap-2 { gap: .5rem; }
.section-title { font-weight: 600; margin-bottom: .5rem; color: #111827; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: .4rem .8rem; align-items: center; }
.grid-span-2 { grid-column: span 2; }
</style>
